import api, { ApiReturn, GetApiReturn, GetApiReturnError, ValidateAxiosError } from '@/modules/api.service';
import { Endpoints } from '@/shared/constants/endpoints';
import { FindMovimentoDto, RelatorioMovimentacoesParametrosDto } from '@/api.types.extra';
import { MovimentacaoRegistrosDto, ReadMovimentacaoDto, RMovimentoDto, RMovimentoRecebidoRegistroDto, UpdateMovimentacaoDto } from '@/api.types';
import { convertToGMTPlus3 } from '@/shared/utils/datas';

export async function LerMovimentacoes(filters: FindMovimentoDto) {
  if (filters) {
    filters.skip ??= 0;
    filters.take ??= 10;
  }

  try {
    const response = await api.get<[ReadMovimentacaoDto[], number]>(Endpoints.MOVIMENTACAO, { params: filters });
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar movimentações(s).');
  }
}

export async function LerMovimentacao(id: number) {
  return api
    .get<ReadMovimentacaoDto>(Endpoints.MOVIMENTACAO + '/' + id)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<ReadMovimentacaoDto>(
        error,
        'Falha ao bucar objeto.' + id,
      );
    });
}

export async function LerRegistrosDoMovimento(movimentoid: number) {
  if (movimentoid != undefined && movimentoid != null) {
    return api
      .get(Endpoints.MOVIMENTACAO + '/damovimentacao/' + movimentoid)
      .then((response) => {
        return GetApiReturn<MovimentacaoRegistrosDto>(
          response.data as Array<MovimentacaoRegistrosDto>,
        );
      })
      .catch((error) => {
        ValidateAxiosError<MovimentacaoRegistrosDto>(
          error,
          'Falha ao listar registros.',
        );
        return GetApiReturnError<MovimentacaoRegistrosDto>();
      });
  } else {
    const r: ApiReturn<MovimentacaoRegistrosDto> = { objects: [], count: 0 };
    return r;
  }
}

export async function AtualizarMovimentacao(movimentacao: UpdateMovimentacaoDto) {
  if (movimentacao.id !== null && movimentacao.id != undefined && movimentacao.id > 0) {
    return api
      .put(Endpoints.MOVIMENTACAO + '/' + movimentacao.id, movimentacao)
      .then(() => {
        return 'Movimentação atualizada.';
      })
      .catch((error) => {
        return ValidateAxiosError<string>(error, 'Falha ao atualizar objeto.');
      });
  } else {
    return api
      .post(Endpoints.MOVIMENTACAO, movimentacao)
      .then(() => {
        return 'Movimentação atualizada.';
      })
      .catch((error) => {
        return ValidateAxiosError<string>(
          error,
          'Falha ao criar movimetanção.',
        );
      });
  }
}

export async function ExcluirMovimentacao(id: number) {
  return api
    .delete(Endpoints.MOVIMENTACAO + '/' + id)
    .then(() => {
      return 'Movimentação removida.';
    })
    .catch((error) => {
      return ValidateAxiosError<string>(error, 'Falha ao remover objeto.');
    });
}

// ---- RELATÓRIOS ---- //
/**
 * Busca o relatório de movimentações.
 * @returns Objeto.
 */
export async function GetRelatorioMovimentacoes(filtros: RelatorioMovimentacoesParametrosDto) {

  // @ts-expect-error property unknow
  const tiposQuery = filtros.query.tipos ? `&${filtros.query.tipos}` : '';
  const queryString = `/${filtros.path.inicio}/${filtros.path.fim}/${filtros.path.ref}?defensor=${filtros.query.defensor}&defensoria=${filtros.query.defensoria}&nucleo=${filtros.query.nucleo}&civel=${filtros.query.civel}&criminal=${filtros.query.criminal}${tiposQuery}`;

  return api
    .get<RMovimentoDto[]>(Endpoints.RELATORIO + '/movimentacoes' + queryString)
    .then((response) => {
      // Corrige o tipo Date do postgres para o tipo Date do javascript (com timezone).
      response.data.forEach((item) => {
        item.recebimentos.forEach((r) => {
          r.referencia = String(convertToGMTPlus3(r.referencia));
        });
      });
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<RMovimentoDto[]>(
        error,
        'Falha ao buscar relatório',
      );
    });
}

export function calcularResumoPorTipo(dados: RMovimentoDto[]): { tipo: string; quantidade: number }[] {
  const retorno = dados
    .flatMap((defensor) =>
      defensor.recebimentos.flatMap((movimento: { registros: RMovimentoRecebidoRegistroDto[] }) =>
        movimento.registros.map((registro) => ({
          tipo: registro.tipo,
          quantidade: Number(registro.quantidade),
        })),
      ),
    )
    .reduce((acc: any, { tipo, quantidade }) => {
      const existente: RMovimentoRecebidoRegistroDto = acc.find((item: RMovimentoRecebidoRegistroDto) => item.tipo === tipo);
      if (existente) {
        existente.quantidade += quantidade;
      } else {
        acc.push({ tipo, quantidade });
      }
      return acc;
    }, []);

  return retorno;
}

export function calcularResumoPorDefensor(dados: RMovimentoDto[]): { tipo: string, quantidade: number }[] {
  const retorno = dados.map(mov => {
    // soma os recebimentos
    const quantidade = mov.recebimentos.reduce((accRec, rec) => {

      // soma os registros dentro de cada recebimento
      const somaRegistros = rec.registros.reduce((accReg, reg) => accReg + (Number(reg.quantidade) ?? 0), 0);
      return accRec + somaRegistros;
    }, 0);

    return { tipo: mov.defensor, quantidade };
  });
  return retorno;
}

export function calcularResumoPorDefensoria(dados: RMovimentoDto[]): { tipo: string; quantidade: number }[] {
  const retorno = dados
    .flatMap((defensor) =>
      defensor.recebimentos.map((movimento) => ({
        tipo: movimento.defensoria || movimento.nucleo,
        quantidade: movimento.registros.reduce((soma, r) => soma + Number(r.quantidade), 0),
      })),
    )
    .reduce((acc: any, { tipo, quantidade }) => {
      const existente = acc.find((item: any) => item.tipo === tipo);
      if (existente) {
        existente.quantidade += quantidade;
      } else {
        acc.push({ tipo, quantidade });
      }
      return acc;
    }, []);

  return retorno;
}


export type ResumoPorDefensoriaETipo = {
  defensoria: string;
  tipos: { tipo: string; quantidade: number }[];
};

export interface ResumoPorDefensoriaETipoEMovimento {
  defensoria: string;
  categorias: {
    categoria: string;
    tipos: {
      tipo: string;
      quantidade: number;
    }[];
  }[];
}

export function calcularResumoPorDefensoriaETipo(dados: RMovimentoDto[]): ResumoPorDefensoriaETipo[] {
  const acumulador: Record<string, Record<string, number>> = {};

  dados.forEach((defensor) => {
    defensor.recebimentos.forEach((movimento) => {
      const defensoria = movimento.defensoria || movimento.nucleo;

      if (!acumulador[defensoria]) acumulador[defensoria] = {};

      movimento.registros.forEach((registro) => {
        const tipo = registro.tipo || "Outro";
        const quantidade = Number(registro.quantidade);

        if (!acumulador[defensoria][tipo]) acumulador[defensoria][tipo] = 0;
        acumulador[defensoria][tipo] += quantidade;
      });
    });
  });

  const resultado: ResumoPorDefensoriaETipo[] = Object.entries(acumulador).map(
    ([defensoria, tiposObj]) => ({
      defensoria,
      tipos: Object.entries(tiposObj).map(([tipo, quantidade]) => ({ tipo, quantidade })),
    }),
  );

  return resultado;
}

export function calcularResumoPorDefensoriaETipoMovimento(dados: RMovimentoDto[]): ResumoPorDefensoriaETipoEMovimento[] {

  const acumulador: Record<string, Record<string, Record<string, number>>> = {};

  dados.forEach((defensor) => {
    defensor.recebimentos.forEach((movimento) => {
      const defensoria = movimento.defensoria || movimento.nucleo;

      // Determina a categoria com base nos flags booleanos
      let categoria = "Outro";
      if (movimento.civel) categoria = "Cível";
      else if (movimento.criminal) categoria = "Criminal";

      if (!acumulador[defensoria]) acumulador[defensoria] = {};
      if (!acumulador[defensoria][categoria])
        acumulador[defensoria][categoria] = {};

      movimento.registros.forEach((registro) => {
        const tipo = registro.tipo || "Outro";
        const quantidade = Number(registro.quantidade);

        if (!acumulador[defensoria][categoria][tipo])
          acumulador[defensoria][categoria][tipo] = 0;

        acumulador[defensoria][categoria][tipo] += quantidade;
      });
    });
  });

  const resultado: ResumoPorDefensoriaETipoEMovimento[] = Object.entries(acumulador).map(
    ([defensoria, categoriasObj]) => ({
      defensoria,
      categorias: Object.entries(categoriasObj).map(([categoria, tiposObj]) => ({
        categoria,
        tipos: Object.entries(tiposObj).map(([tipo, quantidade]) => ({
          tipo,
          quantidade,
        })),
      })),
    })
  );

  return resultado;
}


export function calcularResumoPorNucleo(dados: RMovimentoDto[]): { tipo: string; quantidade: number }[] {
  return dados
    .flatMap((defensor) =>
      defensor.recebimentos.map((movimento) => ({
        tipo: movimento.nucleo, // <-- tratamos núcleo como "tipo"
        quantidade: movimento.registros.reduce((soma, r) => soma + Number(r.quantidade), 0),
      })),
    )
    .reduce((acc: any[], { tipo, quantidade }) => {
      const existente = acc.find((item) => item.tipo === tipo);
      if (existente) {
        existente.quantidade += quantidade;
      } else {
        acc.push({ tipo, quantidade });
      }
      return acc;
    }, []);
}


export const chunkArray = (arr: any[], size: number) =>
  arr.reduce(
    (acc, _, i) => (i % size === 0 ? [...acc, arr.slice(i, i + size)] : acc),
    []
  );

export const corrigeProcessos = (processo?: string) =>
  processo?.split(",").map((p) => p.trim()).join(", ") || '';

export const formatDate = (d: string | Date) =>
  new Intl.DateTimeFormat("pt-BR").format(new Date(d));

export const somarQuantidade = (registros: { quantidade: number }[]) =>
  registros.reduce((ac, r) => ac + Number(r.quantidade), 0);

/**
 * Retorna um texto informando se o registro é cível, criminal ou ambos.
 * @returns
 */
export function textoCivelCriminal(civel: boolean, criminal: boolean) {
  let texto = '';
  if (civel && criminal) texto = 'cível e criminal';
  else if (civel) texto = 'cível';
  else if (criminal) texto = 'criminal';
  else texto = 'sem tipo!';

  return texto;
}


export function processosToArray(p: string): number[] {
  if (!p || p.length <= 0) return [];

  const a = p.split(',').map((e) => Number(e));
  return a;
}

export function processosToString(p: number[]): string {
  if (!p || p.length <= 0) return '';

  const r = p.join(',');
  return r;
}

